import classJoin from 'classjoin';
import {h} from 'preact';
import AbstractVisualStep, {AbstractVisualStepState} from './AbstractVisualStep';
import fetchVisualFloor from './asyncActions/fetchVisualFloor';
import NumberScroll from './NumberScroll';
import sectionScheme from './sectionSchemeSvgPaths.json';
import {
	setStep,
	setTooltip,
	setTooltipVisibility,
} from './Store/creators/visualData';
import {dispatch} from './Store/index';
import {StateVisualFloorMapItem, StateVisualStep} from './Store/State';
import {extractRoomTypeNum, extractRoomTypeText, formatMoney} from './utils/FormatHelpers';

/**
 * Component Properties.
 */
interface VisualFloorProps
{
	/** Корпус */
	building: string;
	/** Секция */
	section: string;
	/** Этаж */
	floor: number;
	/** Путь до изображения */
	image: string;
	/** Карта областей */
	map: StateVisualFloorMapItem[];
	/** Путь до фонового изображения */
	background: string;
	/** Количество этажей в секции */
	floorsCount: number;
}

/**
 * Component State.
 */
interface VisualFloorState extends AbstractVisualStepState
{
	/**
	 * Подписи к областям на карте.
	 */
	labels: VisualFloorStateMapLabel[];
}

/**
 * Подпись к области на карте.
 */
interface VisualFloorStateMapLabel
{
	/** Координата X */
	x: number;
	/** Координата Y */
	y: number;
	/** Число на подписи */
	value: string;
	/** Текст к числу на подписи */
	text: string;
}

/**
 * Общий экран.
 */
class VisualFloor
	extends AbstractVisualStep<VisualFloorProps, VisualFloorState>
{
	/**
	 * Ссылка на элемент карты областей.
	 */
	private mapElement: SVGSVGElement;
	
	/**
	 * Общий экран.
	 */
	public constructor( props: VisualFloorProps )
	{
		super( props );
		
		this.setState(
			{
				labels: [],
			},
		);
	}
	
	/**
	 * Before render, return false to skip render.
	 */
	public shouldComponentUpdate(
		{building, section, floor, image, map, background}: VisualFloorProps,
		{aspectRatioLessThanImage, labels}: VisualFloorState,
	): boolean
	{
		if (
			( map !== this.props.map )
			&& ( labels === this.state.labels )
		)
		{
			this.setState(
				{
					labels: [],
				},
			);
		}
		
		return (
			( building !== this.props.building )
			|| ( section !== this.props.section )
			|| ( floor !== this.props.floor )
			|| ( image !== this.props.image )
			|| ( map !== this.props.map )
			|| ( background !== this.props.background )
			|| ( labels !== this.state.labels )
			|| ( aspectRatioLessThanImage !== this.state.aspectRatioLessThanImage )
		);
	}
	
	/**
	 * Rendered component mounted to the DOM.
	 */
	public componentDidMount(): void
	{
		super.componentDidMount();
		
		if ( this.state.labels.length === 0 )
		{
			this.updateMapLabels();
		}
	}
	
	/**
	 * Render component.
	 */
	public render(
		{
			section, floor, image, map, background, floorsCount,
		}: VisualFloorProps,
		{aspectRatioLessThanImage, labels}: VisualFloorState,
	): JSX.Element
	{
		const floorUri = window.location.pathname + window.location.search;
		const sectionUri = floorUri.replace( /&floor=[^&]+/, '' );
		const buildingUri = sectionUri.replace( /&section=[^&]+/, '' );
		const overviewUri = buildingUri.replace( /&building=[^&]+/, '' );
		
		const emptyMap = ( map.length === 0 );
		
		const compasLeft = 500;
		const compasTop = 670;
		
		return (
			<section class="floor">
				<header>
					<div class="overview">
						<a
							href={overviewUri}
							onClick={this.onOverviewClick}
						>
							План комплекса
						</a>
					</div>
					<h2>
						<a
							class="section"
							href={sectionUri}
							onClick={this.onSectionClick}
						>
							Секция {section}
						</a>
						<span class="separator">, </span>
						<span class="floor">
							Этаж {floor}
						</span>
					</h2>
				</header>
				<div class="scheme">
					{createSectionsScheme( section )}
				</div>
				<NumberScroll
					min={2}
					max={floorsCount}
					value={floor}
					onChange={this.onFloorChange}
				/>
				<div
					class={classJoin(
						{
							[AbstractVisualStep.CLASS_INVERT_ASPECT]: aspectRatioLessThanImage,
							'empty-map': emptyMap,
						},
						['map'],
					)}
					ref={this.refMapContainer}
				>
					<img
						class="background"
						src={background}
						alt=""
					/>
					<img
						src={image}
						alt=""
					/>
					<svg
						width="1920" height="1080"
						viewBox="0 0 1920 1080"
						ref={this.refMap}
					>
						<use xlinkHref="/images/choose-visual/compasses.svg#fixed"
							width="108"
							height="47"
							stroke="#FFF"
							fill="#FFF"
							opacity="0.5"
							transform={`translate(${compasLeft},${compasTop})`}
						/>
						{map.map(
							( area: StateVisualFloorMapItem ) => (
								<path
									d={area.path}
									data-target={area.target}
									data-room-num={extractRoomTypeNum( area.roomType )}
									data-room-type={extractRoomTypeText( area.roomType )}
									data-area={String( area.squareTotal ).replace( '.', ',' )}
									data-cost={
										( area.totalCost > 0 )
										? formatMoney( area.totalCost ) + ' ₽'
										: 'По запросу'
									}
									onClick={this.onAreaClick}
									onMouseEnter={this.onAreaMouseEnter}
									onMouseLeave={this.onAreaMouseLeave}
								/>
							),
						)}
						{labels.map(
							( label ) => (
								<text
									x={label.x}
									y={label.y}
								>
									<tspan>{label.value}</tspan>
									{' '}
									{label.text}
								</text>
							),
						)}
						{
							emptyMap
							? (
								<text class="empty" x="50%" y="50%" text-anchor="middle">
									<tspan dy="-1.2em">На этом этаже</tspan>
									<tspan x="50%" dy="1.2em">нет квартир в продаже</tspan>
								</text>
							)
							: ''
						}
					</svg>
				</div>
			</section>
		);
	}
	
	/**
	 * After render.
	 */
	public componentDidUpdate(): void
	{
		if ( this.state.labels.length === 0 )
		{
			this.updateMapLabels();
		}
	}
	
	/**
	 * При нажатии на область.
	 */
	private onAreaClick = ( event: Event ): void =>
	{
		const target = event.target as Element;
		const targetId = target.getAttribute( 'data-target' );
		
		if ( !targetId )
		{
			return;
		}
		
		dispatch( setTooltipVisibility( false ) );
		
		window.location.href = `/choose/apartment/${targetId}/`;
	}
	
	/**
	 * При наведении курсора на область.
	 */
	private onAreaMouseEnter = ( event: Event ): void =>
	{
		const target = event.target as Element;
		const roomNum = target.getAttribute( 'data-room-num' );
		
		if ( !roomNum )
		{
			return;
		}
		
		const roomType = target.getAttribute( 'data-room-type' ) || '';
		const area = target.getAttribute( 'data-area' ) || '';
		const cost = target.getAttribute( 'data-cost' ) || '';
		
		const title = [
			roomNum + ' ',
			(
				( roomNum === 'Студия' )
				? ''
				: <small>{roomType}</small>
			),
			` ${area} `,
			<small>м²</small>,
		];
		
		dispatch(
			setTooltip(
				title,
				'Стоимость: ' + cost,
			),
		);
	}
	
	/**
	 * При нажатии на «План комплекса».
	 */
	private onOverviewClick = ( event: Event ): void =>
	{
		dispatch( setStep( StateVisualStep.OVERVIEW ) );
		event.preventDefault();
	}
	
	/**
	 * При нажатии на «Секция».
	 */
	private onSectionClick = ( event: Event ): void =>
	{
		dispatch( setStep( StateVisualStep.SECTION ) );
		event.preventDefault();
	}
	
	/**
	 * Сохранить ссылку на элемент карты областей.
	 */
	private refMap = ( element: SVGSVGElement ): void =>
	{
		this.mapElement = element;
	}
	
	/**
	 * При изменении этажа.
	 */
	private onFloorChange = ( value: number ): void =>
	{
		fetchVisualFloor( value );
	}
	
	/**
	 * Обновляет подписи к областям на карте.
	 */
	private updateMapLabels(): void
	{
		const labels: VisualFloorStateMapLabel[] = [];
		const paths = this.mapElement.getElementsByTagName( 'path' );
		
		for ( let i = 0, n = paths.length; i < n; i++ )
		{
			const path = paths[i];
			
			const roomNum = path.getAttribute( 'data-room-num' );
			
			if ( !roomNum )
			{
				continue;
			}
			
			const studio = ( roomNum === 'Студия' );
			
			const roomType = (
				( studio )
				? roomNum
				: path.getAttribute( 'data-room-type' ) || ''
			);
			const bbox = path.getBBox();
			
			labels.push(
				{
					x: Math.round( bbox.x + bbox.width / 2 ),
					y: Math.round( bbox.y + bbox.height / 2 ),
					value: ( studio ? '' : roomNum ),
					text: roomType,
				},
			);
		}
		
		if ( labels.length !== 0 )
		{
			this.setState( {labels} );
		}
	}
}

/**
 * Формирует SVG схему секций.
 * 
 * @param section Номер текущей секции.
 */
function createSectionsScheme( section: string ): JSX.Element
{
	const paths: string[] = sectionScheme.paths;
	const pathElements: JSX.Element[] = [];
	const sectionIndex = Number( section ) - 1;
	
	for ( let i = 0, n = paths.length; i < n; i++ )
	{
		pathElements.push(
			<path
				d={paths[i]}
				class={
					( ( sectionIndex === i ) ? 'current' : '' )
				}
			/>,
		);
	}
	
	return (
		<svg width="289" height="95" viewBox="0 0 289 95">
			{pathElements}
		</svg>
	);
}

/**
 * Module.
 */
export {
	VisualFloor as default,
	createSectionsScheme,
	VisualFloorProps,
	VisualFloorState,
	VisualFloorStateMapLabel,
};



// WEBPACK FOOTER //
// ./app/Routes/Choose/App/VisualFloor.tsx